import {TextArea} from '@dropbox/dig-components/text_fields';
import {Box} from '@dropbox/dig-foundations';
import {Workstream} from 'client';
import {isLexicalJsonString, toPlainTextLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {Title} from 'components/DSYS/Title';
import {t} from 'i18next';
import {$getRoot, type LexicalEditor} from 'lexical';
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {useDescriptionEditor, useWorkstreamEdit} from 'views/workstreams/hooks';

import {
  getWorkstreamDescriptionEditorValue,
  getWorkstreamDescriptionPlainText,
  hasMeaningfulWorkstreamDescriptionContent,
  mergePlainTextIntoRichDescription,
} from './workstreamDescriptionRichText';
import styles from './WorkstreamDetailsShell.module.css';
import {toWorkstreamEditPayload} from './workstreamEditPayload';
import headerStyles from './WorkstreamHeader.module.css';

const serializeEditorDescription = (editor: LexicalEditor | null, fallback: string) => {
  if (!editor) {
    return fallback;
  }

  const serializedDescription = JSON.stringify(editor.getEditorState().toJSON());
  return hasMeaningfulWorkstreamDescriptionContent(serializedDescription)
    ? serializedDescription
    : '';
};

export const WorkstreamDetailsHero = ({
  workstream,
  titleAccessory,
  titleLeading,
  titleTrailing,
}: {
  workstream: Workstream;
  // Rendered before the title, on its first line (e.g. an Archived chip).
  titleAccessory?: ReactNode;
  // Hung left of the title, in the page's gutter (e.g. /workstreams's back
  // arrow); the title and description keep their place.
  titleLeading?: ReactNode;
  // Right after the title's text (e.g. /workstreams's pin); the title then
  // hugs its text instead of filling the row.
  titleTrailing?: ReactNode;
}) => (
  <WorkstreamDetailsHeroContent
    key={workstream.id}
    workstream={workstream}
    titleAccessory={titleAccessory}
    titleLeading={titleLeading}
    titleTrailing={titleTrailing}
  />
);

const WorkstreamDetailsHeroContent = ({
  workstream,
  titleAccessory,
  titleLeading,
  titleTrailing,
}: {
  workstream: Workstream;
  titleAccessory?: ReactNode;
  titleLeading?: ReactNode;
  titleTrailing?: ReactNode;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {editDescription} = useDescriptionEditor(workstream.id);
  const {editWorkstream} = useWorkstreamEdit({invalidateQueries: true});
  const processedAutoEditLocationKeyRef = useRef<string | null>(null);
  const titleEditorRef = useRef<HTMLDivElement | null>(null);
  const descriptionLexicalEditorRef = useRef<LexicalEditor | null>(null);
  const descriptionCaretFrameRef = useRef<number | null>(null);
  const titleSelectionBehaviorOnOpenRef = useRef<'select-all' | 'move-to-end' | null>(null);
  const shouldMoveDescriptionCaretToEndOnOpenRef = useRef(false);
  const serverTitle = workstream.name ?? '';
  const serverDescription = useMemo(
    () => getWorkstreamDescriptionPlainText(workstream.description),
    [workstream.description]
  );
  const serverDescriptionForEditor = useMemo(
    () => getWorkstreamDescriptionEditorValue(workstream.description),
    [workstream.description]
  );

  const [displayTitle, setDisplayTitle] = useState(serverTitle);
  const [draftTitle, setDraftTitle] = useState(serverTitle);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [hasFocusedTitleEditor, setHasFocusedTitleEditor] = useState(false);
  const [pendingSavedTitle, setPendingSavedTitle] = useState<string | undefined>(undefined);
  const [displayDescription, setDisplayDescription] = useState(serverDescription);
  const [draftDescription, setDraftDescription] = useState(serverDescriptionForEditor);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [hasFocusedEditor, setHasFocusedEditor] = useState(false);
  const [pendingSavedDescription, setPendingSavedDescription] = useState<string | undefined>(
    undefined
  );
  const [pendingSavedDescriptionForEditor, setPendingSavedDescriptionForEditor] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (pendingSavedTitle !== undefined) {
      if (serverTitle !== pendingSavedTitle) {
        return;
      }

      setPendingSavedTitle(undefined);
    }

    if (!isEditingTitle) {
      setDisplayTitle(serverTitle);
      setDraftTitle(serverTitle);
    }
  }, [isEditingTitle, pendingSavedTitle, serverTitle]);

  useEffect(() => {
    if (pendingSavedDescription !== undefined) {
      if (serverDescription !== pendingSavedDescription) {
        return;
      }

      setPendingSavedDescription(undefined);
      setPendingSavedDescriptionForEditor(undefined);
    }

    if (!isEditingDescription) {
      setDisplayDescription(serverDescription);
      setDraftDescription(serverDescriptionForEditor);
    }
  }, [
    isEditingDescription,
    pendingSavedDescription,
    serverDescription,
    serverDescriptionForEditor,
  ]);

  const cancelDescriptionCaretFrame = useCallback(() => {
    if (descriptionCaretFrameRef.current === null || typeof window === 'undefined') {
      return;
    }

    window.cancelAnimationFrame(descriptionCaretFrameRef.current);
    descriptionCaretFrameRef.current = null;
  }, []);

  useEffect(() => {
    if (!isEditingDescription) {
      cancelDescriptionCaretFrame();
    }

    return cancelDescriptionCaretFrame;
  }, [cancelDescriptionCaretFrame, isEditingDescription]);

  const handleStartEditing = useCallback(() => {
    cancelDescriptionCaretFrame();
    descriptionLexicalEditorRef.current = null;
    setDraftDescription(pendingSavedDescriptionForEditor ?? serverDescriptionForEditor);
    shouldMoveDescriptionCaretToEndOnOpenRef.current = true;
    setIsEditingDescription(true);
    setHasFocusedEditor(false);
  }, [cancelDescriptionCaretFrame, pendingSavedDescriptionForEditor, serverDescriptionForEditor]);

  const handleStartEditingTitle = useCallback(
    (selectionBehavior: 'select-all' | 'move-to-end' = 'move-to-end') => {
      setDraftTitle(displayTitle);
      titleSelectionBehaviorOnOpenRef.current = selectionBehavior;
      setIsEditingTitle(true);
      setHasFocusedTitleEditor(false);
    },
    [displayTitle]
  );

  const consumeStartTitleEditingState = useCallback(() => {
    if (!location.state || typeof location.state !== 'object') {
      return;
    }

    const remainingState = {
      ...(location.state as {
        startTitleEditing?: boolean;
        [key: string]: unknown;
      }),
    };
    delete remainingState.startTitleEditing;

    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: Object.keys(remainingState).length > 0 ? remainingState : null,
    });
  }, [location.hash, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const shouldStartTitleEditing =
      Boolean(location.state) &&
      typeof location.state === 'object' &&
      Boolean((location.state as {startTitleEditing?: boolean}).startTitleEditing);

    if (
      !shouldStartTitleEditing ||
      isEditingTitle ||
      processedAutoEditLocationKeyRef.current === location.key
    ) {
      return;
    }

    processedAutoEditLocationKeyRef.current = location.key;
    consumeStartTitleEditingState();
    handleStartEditingTitle('select-all');
  }, [
    consumeStartTitleEditingState,
    handleStartEditingTitle,
    isEditingTitle,
    location.key,
    location.state,
  ]);

  useEffect(() => {
    if (
      !isEditingTitle ||
      !titleSelectionBehaviorOnOpenRef.current ||
      typeof window === 'undefined'
    ) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      const titleInput = titleEditorRef.current?.querySelector('textarea');
      if (!titleInput) {
        return;
      }

      titleInput.focus();
      if (titleSelectionBehaviorOnOpenRef.current === 'select-all') {
        titleInput.select();
      } else {
        const end = titleInput.value.length;
        titleInput.setSelectionRange(end, end);
      }
      titleSelectionBehaviorOnOpenRef.current = null;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isEditingTitle]);

  const handleDescriptionEditorReady = useCallback((editor: LexicalEditor) => {
    descriptionLexicalEditorRef.current = editor;

    if (!shouldMoveDescriptionCaretToEndOnOpenRef.current || typeof window === 'undefined') {
      return;
    }

    if (descriptionCaretFrameRef.current !== null) {
      window.cancelAnimationFrame(descriptionCaretFrameRef.current);
    }

    descriptionCaretFrameRef.current = window.requestAnimationFrame(() => {
      descriptionCaretFrameRef.current = null;

      if (descriptionLexicalEditorRef.current !== editor) {
        return;
      }

      editor.update(() => {
        $getRoot().selectEnd();
      });
      shouldMoveDescriptionCaretToEndOnOpenRef.current = false;
    });
  }, []);

  const handleFinishEditingTitle = useCallback(async () => {
    const previousServerTitle = serverTitle;
    const trimmedName = draftTitle.trim();

    setIsEditingTitle(false);

    if (!trimmedName) {
      setDisplayTitle(previousServerTitle);
      setDraftTitle(previousServerTitle);
      setPendingSavedTitle(undefined);
      return;
    }

    if (trimmedName === previousServerTitle.trim()) {
      setDisplayTitle(trimmedName);
      setDraftTitle(trimmedName);
      setPendingSavedTitle(undefined);
      return;
    }

    setDisplayTitle(trimmedName);
    setDraftTitle(trimmedName);
    setPendingSavedTitle(trimmedName);

    try {
      await editWorkstream({
        data: {
          ...toWorkstreamEditPayload(workstream),
          name: trimmedName,
        },
      });
    } catch {
      setPendingSavedTitle(undefined);
      setDisplayTitle(previousServerTitle);
      setDraftTitle(previousServerTitle);
    }
  }, [draftTitle, editWorkstream, serverTitle, workstream]);

  const handleFinishEditing = useCallback(async () => {
    const editorDescription = serializeEditorDescription(
      descriptionLexicalEditorRef.current,
      draftDescription
    );
    const nextDescriptionPlainText = getWorkstreamDescriptionPlainText(editorDescription);
    const previousServerDescription = workstream.description ?? '';
    const previousServerDescriptionPlainText = serverDescription;
    const nextDescription = mergePlainTextIntoRichDescription(
      previousServerDescription,
      nextDescriptionPlainText
    );
    const shouldSkipSave = isLexicalJsonString(previousServerDescription)
      ? nextDescriptionPlainText === previousServerDescriptionPlainText
      : nextDescription === previousServerDescription;

    setIsEditingDescription(false);
    setDisplayDescription(nextDescriptionPlainText);
    setDraftDescription(nextDescription);
    cancelDescriptionCaretFrame();
    descriptionLexicalEditorRef.current = null;

    if (shouldSkipSave) {
      setPendingSavedDescription(undefined);
      setPendingSavedDescriptionForEditor(undefined);
      return;
    }

    setPendingSavedDescription(nextDescriptionPlainText);
    setPendingSavedDescriptionForEditor(nextDescription);

    try {
      await editDescription(nextDescription);
    } catch {
      setPendingSavedDescription(undefined);
      setPendingSavedDescriptionForEditor(undefined);
      setDisplayDescription(previousServerDescriptionPlainText);
      setDraftDescription(getWorkstreamDescriptionEditorValue(previousServerDescription));
    }
  }, [
    cancelDescriptionCaretFrame,
    draftDescription,
    editDescription,
    serverDescription,
    workstream.description,
  ]);

  const handleTitleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleStartEditingTitle();
      }
    },
    [handleStartEditingTitle]
  );

  const handleDescriptionDisplayClick = useCallback(() => {
    handleStartEditing();
  }, [handleStartEditing]);

  const handleDescriptionKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleStartEditing();
      }
    },
    [handleStartEditing]
  );

  const preventWrapperFocus = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const hasDescription = displayDescription.trim().length > 0;
  const showDescriptionEditor = isEditingDescription || !hasDescription;

  // Baseline, so the accessory sits on a wrapped title's first line. The gap
  // clears the title's hover tint, which bleeds 6px past its text.
  const titleRow = (
    <Box display="flex" alignItems="baseline" style={{gap: 12}}>
      {titleAccessory ? <Box style={{flexShrink: 0}}>{titleAccessory}</Box> : null}
      {isEditingTitle ? (
        <div
          ref={titleEditorRef}
          className={`${styles.heroTitleEditor} ${styles.textContentMaxWidth}`}
          data-testid="workstream-details-hero-title-editor"
        >
          <TextArea
            autoFocus
            rows={2}
            value={draftTitle}
            className={`${styles.heroTextArea} ${styles.heroTitleTextArea} ${headerStyles.headerTitleInput}`}
            onChange={(event) => setDraftTitle(event.currentTarget.value)}
            onFocus={() => setHasFocusedTitleEditor(true)}
            onBlur={() => {
              if (hasFocusedTitleEditor) {
                void handleFinishEditingTitle();
              }
            }}
          />
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          data-testid="workstream-details-hero-title"
          className={`${styles.heroTitleDisplay} ${styles.textContentMaxWidth}${titleTrailing ? ` ${styles.heroTitleDisplayHug}` : ''}`}
          onMouseDown={preventWrapperFocus}
          onClick={() => handleStartEditingTitle()}
          onKeyDown={handleTitleKeyDown}
        >
          <Title size={18} className={`${styles.heroTitle} ${headerStyles.headerTitle}`}>
            <span className={headerStyles.headerTitleText}>{displayTitle}</span>
          </Title>
        </div>
      )}
      {titleTrailing ? (
        <Box display="flex" alignItems="center" style={{flexShrink: 0, alignSelf: 'center'}}>
          {titleTrailing}
        </Box>
      ) : null}
    </Box>
  );

  return (
    <div className={styles.hero}>
      {titleLeading ? (
        // Hangs in the gutter left of the title (8px clears the title's -6px
        // hover tint). Absolutely placed and centered in a box one title line
        // tall (Title size 18's 26px), so it lines up with the pin and sits on a
        // wrapped title's first line, and the title, description, and properties
        // all start at the same x.
        <Box position="relative" display="flex" alignItems="flex-start" style={{minWidth: 0}}>
          <Box
            position="absolute"
            display="flex"
            alignItems="center"
            style={{top: 0, height: 26, right: '100%', marginRight: 8}}
          >
            {titleLeading}
          </Box>
          <Box style={{flex: 1, minWidth: 0}}>{titleRow}</Box>
        </Box>
      ) : (
        titleRow
      )}

      <div
        className={`${styles.heroSummary} ${styles.textContentMaxWidth} ${
          showDescriptionEditor ? '' : styles.heroSummaryFilled
        }`}
      >
        {showDescriptionEditor ? (
          <>
            <div className={styles.heroSummaryEditor} data-testid="workstream-details-hero-editor">
              <RichTextArea
                key={
                  hasDescription
                    ? 'workstream-objective-editor-filled'
                    : 'workstream-objective-editor-empty'
                }
                autoFocus={false}
                editable
                value={draftDescription}
                placeholder={t('add_workstream_objective')}
                hideToolbar
                links={false}
                mentions={false}
                disableTables
                disableBorder
                minHeight={24}
                editablePaddingX="0"
                editablePaddingY="0"
                placeholderOffsetX={0}
                contentClassName={styles.heroPlainRichTextContent}
                onEditorReady={handleDescriptionEditorReady}
                onChange={(editorState) => setDraftDescription(editorState.toString())}
                onToggleFocused={(focused) => {
                  if (focused) {
                    setHasFocusedEditor(true);
                    return;
                  }

                  if (hasFocusedEditor) {
                    void handleFinishEditing();
                  }
                }}
              />
            </div>
          </>
        ) : (
          <>
            <div
              role="button"
              tabIndex={0}
              data-testid="workstream-details-hero-description"
              className={styles.heroSummaryDisplay}
              onMouseDown={preventWrapperFocus}
              onClick={handleDescriptionDisplayClick}
              onKeyDown={handleDescriptionKeyDown}
            >
              <RichTextArea
                editable={false}
                value={toPlainTextLexicalJson(displayDescription)}
                squareBorders
                textColor="subtle"
                truncateLines={2}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
